"use client";

import { isFastly } from "@faire/web/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useRenderTracing } from "@faire/web/common/tracing/useRenderTracing";
import React, { useContext } from "react";

import { ROWS_PER_BATCH } from "@faire/retailer/components/SearchProducts/SingleProductRowPlaceholder";
import { ProductImage } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductImage/ProductTileImage";
import { searchPageProductTileImagePropsAdapter } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductImage/search/searchPageProductTileImagePropsAdapter";
import { SearchProductImageProps } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductImage/search/SearchProductImageProps";
import { ProductPositioningContext } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/productTileContext";
import { ProductTileTypeContext } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/productTileTypeContext";
import { getValidImageOrPlaceholder } from "@faire/retailer/features/MarketplaceItems/ProductDetailsTile/getValidImageOrPlaceholder";
import { useValidateImage } from "@faire/retailer/features/MarketplaceItems/ProductDetailsTile/useValidateImage";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { useSearchPageLoggedOutBrandTileReviews } from "@faire/retailer/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/DataFetching/LoggedOutBrandTileReviews/useSearchPageLoggedOutBrandTileReviews";
import { useSearchPageIsFromDiscoverURL } from "@faire/retailer/views/search-v2/SearchPageQueryParams/useSearchPageIsFromDiscoverURL";

export const SearchProductImageClient = ({
  data: {
    productToken,
    productName,
    image,
    productSearchString,
    spellCorrectedQuery,
  },
}: {
  data: SearchProductImageProps;
}) => {
  const endTrace = useRenderTracing("ProductTileImage", {
    productToken: productToken,
  });
  const { strictLocalize } = useStrictLocalization();
  const { productTileType } = useContext(ProductTileTypeContext);
  const isFromDiscoverURL = useSearchPageIsFromDiscoverURL();
  const { reviews } = useSearchPageLoggedOutBrandTileReviews();
  const productPositioningContext = React.useContext(ProductPositioningContext);
  const useLazyImageLoading =
    productPositioningContext.index !== undefined &&
    productPositioningContext.itemsPerRow !== undefined &&
    productPositioningContext.index >=
      productPositioningContext.itemsPerRow * ROWS_PER_BATCH;
  let primaryImage = image;
  const validImage = useValidateImage(image);
  if (image?.url && !isFastly(image.url)) {
    primaryImage = getValidImageOrPlaceholder(validImage);
  }

  const searchProductTileImageProps = searchPageProductTileImagePropsAdapter({
    productName,
    strictLocalize,
    productTileType,
    productSearchString,
    spellCorrectedQuery,
    isFromDiscoverURL,
    reviews,
    shouldUseLazyImageLoading: useLazyImageLoading,
    showVerticalImagery: false,
    primaryImage,
    onLoad: endTrace,
  });

  return <ProductImage data={searchProductTileImageProps} />;
};
